"use client";

import { StickyNote } from "lucide-react";
import { css, cx } from "styled-system/css";

export type CalendarAppointment = {
  id: string;
  clientName: string;
  serviceName: string;
  status: string;
  amountPaid: number;
  depositStatus: string | null;
  reminderSent24?: boolean;
  reminderSent1h?: boolean;
  /** Sobreturno: turno intencionalmente solapado con otro. */
  isOverbook?: boolean;
  /** Un envío de WhatsApp para este turno falló con error 63024 (el número no tiene WhatsApp). */
  whatsappUnavailable?: boolean;
  /** Título/descripción original del evento de Google Calendar, si el turno vino de ahí. */
  originalGoogleTitle?: string | null;
};

function showAmount(a: CalendarAppointment) {
  return a.amountPaid != null && a.amountPaid > 0;
}

function buildSummary(a: CalendarAppointment, whatsapp: boolean): string {
  const parts = [a.clientName, a.serviceName];
  if (a.amountPaid != null && a.amountPaid > 0) parts.push(`$${Math.round(a.amountPaid)}`);
  if (a.depositStatus === "pending") parts.push("Seña pendiente");
  if (a.whatsappUnavailable) parts.push("Sin WhatsApp");
  if (whatsapp) parts.push("WhatsApp enviado");
  if (a.originalGoogleTitle) parts.push(`Google: ${a.originalGoogleTitle}`);
  return parts.join(" · ");
}

export function EventCard({ appointment, agendaName, agendaColor }: { appointment: CalendarAppointment; agendaName?: string; agendaColor?: string }) {
  const whatsapp = Boolean(appointment.reminderSent24 || appointment.reminderSent1h);
  const summary = buildSummary(appointment, whatsapp);
  return (
    <div className="cf-event" data-status={appointment.status} title={summary}>
      <div className="cf-event__bar" style={agendaColor ? { backgroundColor: agendaColor } : undefined} />
      <div className="cf-event__body">
        {agendaName ? (
          <span
            className={css({ fontSize: "10px", fontWeight: "semibold", mb: "0.5", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })}
            style={{ color: agendaColor ?? "#6366f1" }}
          >
            {agendaName}
          </span>
        ) : null}
        <span className="cf-event__client">
          {appointment.isOverbook ? (
            <span
              className={css({ fontSize: "9px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.04em", px: "1", py: "0.5", rounded: "sm", mr: "1", bg: "#fef3c7", color: "#92400e" })}
              title="Sobreturno (solapado con otro turno)"
            >
              Sobreturno
            </span>
          ) : null}
          {appointment.whatsappUnavailable ? (
            <span
              className={css({ fontSize: "9px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.04em", px: "1", py: "0.5", rounded: "sm", mr: "1", bg: "#fef2f2", color: "#dc2626" })}
              title="Un envío de WhatsApp a este número falló: no tiene WhatsApp"
            >
              ⚠️ Sin WhatsApp
            </span>
          ) : null}
          {appointment.clientName}
        </span>
        <span className="cf-event__service">{appointment.serviceName}</span>
        {appointment.originalGoogleTitle ? (
          <span className="cf-event__original" title={appointment.originalGoogleTitle}>
            <StickyNote className="cf-event__original-icon" size={10} strokeWidth={2} aria-hidden="true" />
            {appointment.originalGoogleTitle}
          </span>
        ) : null}
        <div className="cf-event__meta">
          {showAmount(appointment) ? <span className="cf-event__amount">${Math.round(appointment.amountPaid)} ✓</span> : null}
          {appointment.depositStatus === "pending" ? <span>Seña pendiente</span> : null}
          {whatsapp ? <span className={css({ fontWeight: "semibold" })}>WhatsApp ✓</span> : null}
        </div>
      </div>
    </div>
  );
}

function GoogleGIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className={css({ flexShrink: 0 })}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

/**
 * Mismo tratamiento visual que EventCard (misma clase `cf-event`: barra de
 * color a `1fr`, mismo padding, mismo truncate) para que los eventos de
 * Google no se vean ni se corten distinto a los turnos normales.
 */
export function GoogleEventCard({
  title,
  color,
  calendarName,
  zeroDuration,
}: {
  title: string;
  color?: string | null;
  calendarName?: string | null;
  zeroDuration?: boolean;
}) {
  const evColor = color ?? "#4285F4";
  return (
    <div
      className="cf-event"
      title={zeroDuration ? `${title} (sin horario de fin definido en Google)` : title}
    >
      <div className="cf-event__bar" style={{ backgroundColor: evColor, opacity: zeroDuration ? 0.5 : 1 }} />
      <div className="cf-event__body">
        {calendarName ? (
          <span
            className={css({ fontSize: "10px", fontWeight: "semibold", mb: "0.5", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })}
            style={{ color: evColor }}
          >
            {calendarName}
          </span>
        ) : null}
        <span className={css({ display: "flex", alignItems: "center", gap: "1", minW: 0 })}>
          <GoogleGIcon />
          <span className={cx("cf-event__client", css({ minW: 0 }))}>{title}</span>
        </span>
      </div>
    </div>
  );
}

